var _         = require('lodash');
var Rx        = require('ub_wrapped/rx');

var ajaxAsObservable = require('ub/control/ajax-as-observable');
var ubBanzai         = window.ubBanzai;
var endpoints        = require('ub/config/endpoints').default;
var LoginModalController = require('ub/ui/login/state-controller').default;

var validPublishingStates = [
  'republishing',
  'publishing'
];

var validTransitionStates = [
  'unpublishing'
].concat(validPublishingStates);

var validPublishedStates = [
  'published',
  'publishedWithChanges',
  'publishedWithoutChanges'
];

var validUnpublishedStates = [
  'unpublished',
  'neverPublished'
];

var validPublishOutcomeStates =[
  'publishSuccess',
  'publishError',
  'republishSuccess',
  'republishError'
];

var allValidPageStates = [].concat(validTransitionStates, validPublishOutcomeStates,
  validPublishedStates, validUnpublishedStates);

var _isPublishedOrUnpublishedState = function(state) {
  return _.contains(validPublishedStates.concat(validUnpublishedStates), state);
};

var _isPublishingState = function(state) {
  return _.contains(validPublishingStates, state);
};

var _isTransitionState = function(state) {
  return _.contains(validTransitionStates, state);
};

var _mkRequest = function(companyId, pageUUID) {
  var pollerHostname = ubBanzai.getFeatureValue('pollerHostname');
  var webappPagesResource = window.editor.mainPage.getConfigValue('webappPagesResource');
  var url = endpoints.info(pollerHostname, webappPagesResource, companyId, pageUUID);

  return ajaxAsObservable({
      url      : url,
      dataType : 'json'
    })
    .timeout(ubBanzai.getFeatureValue('republishTimeoutPeriod'))
    .catch(function(e) {
      if (ubBanzai.features.isDebugModeEnabled()) {
        console.warn('PagePublishObserver:', 'caught AJAX error', e);
      }

      if (ubBanzai.features.isNewLoginModalEnabled()) {
        LoginModalController.notifySessionExpire(e.jqXHR.status);
      }

      return Rx.Observable.return(null);
    });
};

var _mkPoller = function(companyId, uuid, queryInterval, skipInitial) {
  var getJitter = function() {
    return _.random(0, queryInterval / 10 );
  };

  var intervalRequestObs = Rx.Observable
    .interval(queryInterval + getJitter()) // avoid the stampeding herd
    .concatMap(function() {
      return Rx.Observable
        .return(null)
        .delay(getJitter())
        .concat(_mkRequest(companyId, uuid))
        .filter(_.identity);
    });

  if (skipInitial) {
    return intervalRequestObs;
  } else {
    var initialRequestObs = Rx.Observable
      .concat(_mkRequest(companyId, uuid));
    return Rx.Observable.concat(initialRequestObs, intervalRequestObs);
  }

};

var _getPageStateFromServer = function(publishStatesFromServer, page) {
  var webappPayloadKey = page.getConfigValue('webappPayloadKey');

  return publishStatesFromServer
    .filter(_.identity) // Filter out failed AJAX requests
    .map(function ajaxRespToPageState(ev) {
      var pageData = ev.data[webappPayloadKey];

      // Update the page URL and title
      if (page) {
        if ( pageData.full_url ) { page.published_url = pageData.full_url; }
        if ( pageData.name ) { page.title = pageData.name; }
      }

      // Process the publish state
      if (pageData.state === 'published') {
        var updatedAt             = new Date(pageData.modified_at)
          , lastPublishedAt       = new Date(pageData.last_published_at)
          , hasUnpublishedChanges = updatedAt > lastPublishedAt;
        return {
          type              : hasUnpublishedChanges ? 'publishedWithChanges' : 'publishedWithoutChanges',
          lastPublishFailed : pageData['last_publish_failed?']
        };
      } else if (pageData.state === 'unpublished') {
        return {
          type              : pageData.last_published_at ? 'unpublished' : 'neverPublished',
          lastPublishFailed : pageData['last_publish_failed?']
        };
      } else {
        return {
          type: pageData.state
        };
      }
    })
    .filter(function filterUnknownStates(state){
      return _.contains(allValidPageStates, state.type);
    });
};


var Poller = function(companyId, pageUUID) {
  this.events              = new Rx.Subject();
  this._activeSubscription = null;
  this._companyId          = companyId;
  this._pageUUID           = pageUUID;
};

Poller.prototype.isEnabled = function() {
  return Boolean(this._activeSubscription);
};

Poller.prototype.enable = function(interval, skipInitial) {
  if (ubBanzai.features.isSandboxEnabled()) {return;}

  if (this._activeInterval !== interval) {
    this.disable();
    var obs = _mkPoller(this._companyId, this._pageUUID, interval, skipInitial);
    this._activeSubscription = obs.ub_subscribe(this.events.onNext.bind(this.events));
    this._activeInterval     = interval;

    if (ubBanzai.features.isDebugModeEnabled()) {
      console.info('PagePublishObserver:', 'Poller activated at interval', interval);
    }
  }
};

Poller.prototype.disable = function() {
  if (this._activeSubscription && ! this._activeSubscription.isDisposed) {
    this._activeSubscription.dispose();
    this._activeSubscription = null;
    this._activeInterval     = null;
  }
};


var PagePublishObserver = function(companyId, page) {
  if (! ubBanzai.features.isRepublishingEnabled()) { return; }

  this.uiEvents = new Rx.Subject(); // The UI calls .onNext on this

  var fastInterval = ubBanzai.getFeatureValue('publishStatePollerFast'),
      slowInterval = ubBanzai.getFeatureValue('publishStatePollerSlow'),
      poller       = new Poller(companyId, page.uuid);

  poller.enable(slowInterval);

  var serverStates = _getPageStateFromServer(poller.events, page);

  this.state = this.uiEvents
    .merge(serverStates)
    .scan(function checkForTransition(current, next){
      if (_isPublishingState(current.type) && _isPublishedOrUnpublishedState(next.type)) {
        // Transforms the event type to:
        // publishSuccess, publishError, republishSuccess, or republishError
        var typePrefix = current.type === 'publishing' ? 'publish' : 'republish';
        return {
          type: typePrefix + ( next.lastPublishFailed ? 'Error' : 'Success' )
        };
      } else {
        return next;
      }
    })
    .distinctUntilChanged()
    .publish(); // we call .connect before exiting the ctor

  this.state.subscribe(function manageTransitionPolling(ev) {
    if (ev.type === 'requestRepublish') {
      // Reset polling when the republish trigger request is initiated
      // to avoid race-condition. We don't completely disable it as
      // there is a chance of getting stuck in this state and the
      // corresponding modal dialog has no close button. There is
      // still a small chance of a race between the poller and the
      // requestPublish POST requests to the server (if they take a
      // long time). The UI flicker that would result isn't as bad as
      // getting stuck here.
      poller.enable(slowInterval, true);
      if (ubBanzai.features.isDebugModeEnabled()) {
        console.info('PagePublishObserver:', 'Poller disabled');
      }
    } else if (ev.type === 'republishInitiated' || _isTransitionState(ev.type)) {
      // Enable fast poll after republish trigger was successful or if a publish,
      // republish or unpublish was initiated from outside the builder
      poller.enable(fastInterval);
    } else {
      // Enable the slow poller in all other cases
      poller.enable(slowInterval);
    }
  });

  // Debug log
  if (ubBanzai.features.isDebugModeEnabled()) {
    this.state.ub_subscribe(console.info.bind(console, 'PagePublishObserver:'));
  }

  this.state.connect();
};

module.exports = PagePublishObserver;



// WEBPACK FOOTER //
// ./src/lp/editor/page-publish-observer.js